import { Loader2 } from "lucide-react";
import { MANDATORY, matchClass, pretty } from "@/lib/sourcing";
import { cn } from "@/lib/utils";

export const MATCH_CLS = {
  Exceptional: "text-[#34D399] border-[#10B981]/40 bg-[#064E3B]/25",
  Strong: "text-[#D4AF37] border-[#D4AF37]/40 bg-[#D4AF37]/10",
  Moderate: "text-[#FBBF24] border-[#D97706]/40 bg-[#78350F]/20",
  Weak: "text-[#F97316] border-[#EA580C]/40 bg-[#7C2D12]/20",
  Low: "text-[#EF4444] border-[#DC2626]/40 bg-[#7F1D1D]/20",
};
const RING = { Exceptional: "#34D399", Strong: "#D4AF37", Moderate: "#FBBF24", Weak: "#F97316", Low: "#EF4444" };
const VERIF_CLS = {
  VERIFIED: "bg-[#064E3B]/70 text-[#34D399] border-[#10B981]/40", CORROBORATED: "bg-[#1E1B4B]/70 text-[#A78BFA] border-[#8224E3]/40",
  INFERRED: "bg-[#1F2937]/70 text-[#9CA3AF] border-[#4B5563]/40", UNKNOWN: "bg-[#27272A]/70 text-[#71717A] border-[#3F3F46]/40",
  CONFLICTING: "bg-[#7F1D1D]/70 text-[#FCA5A5] border-[#EF4444]/40", NEEDS_VERIFICATION: "bg-amber-500/10 text-amber-300 border-amber-500/40",
};
const MAND_CLS = { all_supported: "bg-[#10B981]/15 text-[#34D399] border-[#10B981]/30", some_unverified: "bg-[#F59E0B]/15 text-[#FBBF24] border-[#F59E0B]/30", not_met: "bg-[#EF4444]/15 text-[#F87171] border-[#EF4444]/30 font-semibold", conflicting: "bg-[#8224e3]/15 text-[#d7b6ff] border-[#8224e3]/40" };

export const ScoreRing = ({ v, size = 44, label, testid }) => {
  const c = matchClass(v);
  const r = size / 2 - 4, len = 2 * Math.PI * r;
  return (
    <div className="flex flex-col items-center" data-testid={testid}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#27272A" strokeWidth="4" fill="none" />
        {v != null && <circle cx={size / 2} cy={size / 2} r={r} stroke={RING[c]} strokeWidth="4" fill="none" strokeLinecap="round" strokeDasharray={len} strokeDashoffset={len * (1 - v / 100)} className="transition-[stroke-dashoffset] duration-700" />}
      </svg>
      <span className="-mt-[30px] mb-[10px] font-mono text-xs font-semibold text-zinc-100" style={{ height: 20 }}>{v ?? "—"}</span>
      {label && <span className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</span>}
    </div>
  );
};

export const MatchChip = ({ v }) => { const c = matchClass(v); return c ? <span className={cn("rounded-full border px-2 py-0.5 text-[10px] font-semibold", MATCH_CLS[c])}>{c}</span> : null; };
export const Verif = ({ v }) => <span className={cn("rounded border px-1.5 py-0.5 font-mono text-[10px]", VERIF_CLS[v] || VERIF_CLS.UNKNOWN)}>{String(v || "UNKNOWN").replace(/_/g, " ")}</span>;
export const Mandatory = ({ v }) => v ? <span className={cn("whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px]", MAND_CLS[v])}>{MANDATORY[v] || pretty(v)}</span> : <span className="text-[10px] text-zinc-600">Not scored</span>;

export const Metric = ({ k, v, tone, testid }) => (
  <div className="rounded-xl border border-[#27272A] bg-[#121215] p-4 transition-colors duration-200 hover:border-[#D4AF37]/40" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">{k}</p>
    <p className={cn("mt-1 font-display text-2xl text-zinc-50", tone)}>{v ?? "—"}</p>
  </div>
);

export const Panel = ({ title, right, children, className, testid }) => (
  <section className={cn("min-w-0 rounded-2xl border border-[#27272A] bg-[#0f0f12] p-4 sm:p-5", className)} data-testid={testid}>
    {(title || right) && <div className="mb-3 flex flex-wrap items-center justify-between gap-2"><h3 className="font-display text-base text-zinc-100">{title}</h3>{right}</div>}
    {children}
  </section>
);

export const EmptyBox = ({ icon: I, title, text, children, testid }) => (
  <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[#27272A] p-10 text-center" data-testid={testid}>
    {I && <I className="h-6 w-6 text-[#8224e3]" />}<p className="text-sm font-semibold text-zinc-300">{title}</p>{text && <p className="max-w-md text-xs text-zinc-500">{text}</p>}{children}
  </div>
);

export const Missing = ({ what }) => (
  <div className="rounded-xl border border-dashed border-[#8224e3]/50 bg-[#8224e3]/5 p-4 text-xs text-zinc-400" data-testid="si-pending"><span className="font-semibold text-[#d7b6ff]">Waiting for the backend. </span>{what} Nothing here is simulated; this fills in when the route is live (backend-spec §35).</div>
);

export const Busy = ({ on }) => (on ? <Loader2 className="h-4 w-4 animate-spin" /> : null);

export const Chip = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} className={cn("rounded-full border px-3 py-1 text-xs transition-colors duration-150", active ? "border-[#D4AF37] bg-[#D4AF37] font-semibold text-black" : "border-[#27272A] text-zinc-400 hover:border-[#D4AF37]/50 hover:text-zinc-100")} data-testid={testid}>{children}</button>
);
